<script setup lang="ts">
const props = withDefaults(
  defineProps<{
    article: ArticleCard
    widthFactor?: ColumnsArticlesType
  }>(),
  {
    widthFactor: 3,
  },
)

const columns = computed<string>(() => {
  if (props.widthFactor === 2) {
    return 'two'
  }
  if (props.widthFactor === 3) {
    return 'three'
  }
  return 'four'
})
</script>

<template>
  <NuxtLink
    v-if="article"
    class="group relative flex h-full items-center justify-center self-stretch overflow-hidden no-underline transition-all hover:contrast-75 focus:border-4 focus:border-blue-50 md:max-h-none"
    :class="[
      `article-card--${columns}-cols`,
      article?.colorTheme,
      widthFactor === 4 ? 'sm:flex-col' : 'flex-col',
    ]"
    :to="article.slug.full"
  >
    <figure class="article-card__figure">
      <CrystallizeImage
        v-if="article.thumbnailImage || article.cover"
        :image="article.thumbnailImage || article.cover"
        class="size-full"
        :sizes="widthFactor === 4 ? '200px' : '500px'"
        img-class="size-full group-hover:scale-105 object-cover transition-transform duration-300 ease-in-out"
        :class="{
          'flex items-stretch justify-center border-b-2 border-transparent':
            widthFactor === 4,
        }"
      />
      <div v-else>
        <IconsGraphicsLetter
          :letter="getFirstLetter(article.title)"
          :color-theme="article.colorTheme"
          :class="widthFactor === 4 ? 'group-hover:scale-110' : 'group-hover:scale-105'"
          class="transition-transform"
        />
      </div>
    </figure>
    <div
      class="article-card__text-container z-5 max-h-26 w-full bg-(--background) py-4 text-white transition-all group-hover:max-w-full md:max-w-120"
      :class="{
        'article-card__text-container--top-translate -mt-8': widthFactor === 2,
      }"
    >
      <span class="text-container__tags block text-center">
        <UiCommonsTag
          :class="widthFactor === 4 ? 'justify-start sm:justify-center' : 'justify-center'"
          :article="article"
        />
      </span>
      <h3 class="article-card__title" v-text="article.title" />
    </div>
  </NuxtLink>
</template>

<style scoped>
@reference "~/assets/styles/main.css";

.article-card__title {
  @apply text-base text-center;
  margin: 0.2rem 1rem 1rem 0;
}

.article-card__figure {
  width: 100%;
  height: 100%;
  overflow: hidden;
  min-height: 10rem;
  flex-grow: 1;
}

.article-card--two-cols {
  width: min(95%, 24rem);
  height: 28rem;

  @media screen and (min-width: theme('screens.xl')) {
    width: 35rem;
    height: 28rem;
  }

  &:hover {
    .article-card__text-container {
      padding: 2rem;

      @media screen and (min-width: theme('screens.xl')) {
        padding-inline: 4.4rem;
      }
    }
  }

  .article-card__text-container {
    padding: 1.4rem;
    min-height: 10rem;
    max-width: 20rem;

    @media screen and (min-width: theme('screens.xl')) {
      padding: 2rem;
      max-width: 30rem;
    }

    .article-card__title {
      @apply text-2xl-mobile font-semibold xl:text-2xl;
    }
  }
  .article-card__text-container--top-translate {
    margin-top: 0;
    transform: translateY(-4rem);

    @media screen and (min-width: theme('screens.sm')) {
      margin-top: -2rem;
      transform: translateY(0px);
    }
  }
}

.article-card--three-cols {
  width: 80%;
  height: 18.5rem;
  flex-flow: column nowrap;
  justify-content: space-between;
  background-color: var(--foreground);

  @media screen and (min-width: theme('screens.sm')) {
    width: 15rem;
    height: 22rem;
  }

  @media screen and (min-width: theme('screens.xl')) {
    width: 22rem;
    height: 19rem;
  }

  .article-card__text-container {
    padding: 1rem 1rem;
    min-height: 8rem;

    @media screen and (min-width: theme('screens.sm')) {
      min-height: 10rem;
    }

    @media screen and (min-width: theme('screens.xl')) {
      padding: 1rem 2rem;
      height: 19rem;
      min-height: 8rem;
    }

    .article-card__title {
      @apply text-lg-snug font-semibold my-2;
    }
  }

  .article-card__figure {
    padding: 0;
    min-height: 8rem;

    @media screen and (min-width: theme('screens.xl')) {
      min-height: 10rem;
    }
  }
}

.article-card--four-cols {
  background-color: var(--background);

  .article-card__figure {
    flex: 1;
    min-height: 4rem !important;
    background-color: var(--foreground);
  }

  .article-card__text-container {
    flex: 2;
    padding: 0.2rem 0.5rem;
    height: 100%;

    @media screen and (min-width: theme('screens.sm')) {
      min-height: 8rem;
    }

    .text-container__tags,
    .article-card__title {
      @apply text-lg font-medium text-left sm:text-center sm:my-2 sm:mx-4 sm:leading-7;
    }
  }

  @media screen and (min-width: theme('screens.sm')) {
    width: 16.85rem;
    height: 16.5rem;

    .article-card__figure {
      flex: 1;
    }

    .article-card__text-container {
      flex: 1;
    }
  }
}
</style>
